// =============================================================================
// ui/state/useAutosave.ts — commit on blur, and flush before the screen goes.
// -----------------------------------------------------------------------------
// 🔴 WHY THIS EXISTS. Josh wrote a premarket plan, clicked the Session phase,
// came back, and the plan was the last SAVED one (TCR-79). Two faults compounded:
// the form only wrote when its Save button was pressed, and the phase switch
// unmounts the panel — so the remount re-seeded the draft from disk.
//
// His rule, and it is the whole specification: *"This should auto save all the
// time, on any tab where the user manually inputs text. We shouldn't ever lose
// data… I don't want any user to ever feel like they wasted their time. It
// significantly impacts the user's faith in the product."*
//
// ── ⚠ THE MECHANISM IS NOT NEW HERE, AND THAT IS DELIBERATE ──
//
// Commit-on-blur already shipped in six modules before this hook existed:
// `Backtests.tsx` (`onBlur={() => commit(draft)}`, seven handlers),
// `Practice.tsx`, `ConfluenceEditor.tsx`, `CustomTagEditor.tsx`,
// `MistakeTagEditor.tsx` and `Settings.tsx`. This lifts the shape they already
// use rather than inventing a seventh — and it does NOT touch those six. They
// work; a refactor of working code is a separate decision.
//
// ── 🔴 WHAT THE UNMOUNT FLUSH IS FOR, AND WHAT IT IS NOT ──
//
// Blur alone very nearly closes the hole: clicking a sibling tab usually blurs
// the focused field first. **Usually is not a guarantee**, and the failure is
// silent — an edit made by a control that never took focus, or a browser that
// collapses blur into teardown, loses the work with no error and no trace.
//
// ⚠ IT PERSISTS THE WORK; IT DOES NOT PRESERVE THE DRAFT. Nothing survives the
// unmount, on purpose. Keeping a draft alive across a remount is what D61 is
// about: `Journal.tsx` passes `key={date}` precisely so stepping to another day
// CANNOT carry yesterday's form over a real row — observed live on 7/31 → 7/30,
// where one Save would have written blanks over a real plan. Writing before the
// teardown sidesteps that question entirely instead of re-opening it.
// =============================================================================

import { useCallback, useEffect, useRef } from 'react';

export interface AutosaveOptions {
  /** Is there anything worth writing? Nothing is written when this is false. */
  dirty: boolean;
  /**
   * Perform the write. May be async; rejections are swallowed here — the caller
   * owns its own error surface, and an unmount flush has no screen to report on.
   */
  commit: () => void | Promise<unknown>;
  /** Set false to disable entirely (a read-only or locked form). Default true. */
  enabled?: boolean;
}

export interface Autosave {
  /** Hang this on every field: `onBlur={autosave.onBlur}`. */
  onBlur: () => void;
  /** Write now if dirty. Exposed for a control that commits without blurring. */
  flush: () => void;
}

export function useAutosave(opts: AutosaveOptions): Autosave {
  const { dirty, commit, enabled = true } = opts;

  // ⚠ REFS, NOT THE CLOSURE. The unmount cleanup below runs once, with whatever
  // it captured — and what it captured would be the FIRST render's `dirty` and
  // `commit`, so it would flush nothing on the only path that matters. A ref
  // read at teardown sees the last render instead.
  const dirtyRef = useRef(dirty);
  const commitRef = useRef(commit);
  const enabledRef = useRef(enabled);
  dirtyRef.current = dirty;
  commitRef.current = commit;
  enabledRef.current = enabled;

  const flush = useCallback(() => {
    if (!enabledRef.current || !dirtyRef.current) return;
    // ⚠ Not awaited, and the rejection is swallowed rather than unhandled: on
    // the unmount path there is no component left to show an error on, and an
    // unhandled rejection in a teardown is a console error the user cannot act
    // on. A failed autosave leaves the form dirty, so the next blur retries.
    void Promise.resolve(commitRef.current()).catch(() => {});
  }, []);

  // 🔴 THE FLUSH, AND THE EMPTY DEPENDENCY LIST IS LOAD-BEARING. With `flush` or
  // `dirty` in the deps this effect would tear down and re-run on every edit —
  // firing a write on each keystroke rather than once on the way out.
  useEffect(() => () => flush(), [flush]);

  return { onBlur: flush, flush };
}
